/**
 * The MSP-wide price list: parts, software licenses and fixed-price services.
 *
 * The port of `Views/CatalogPage.xaml` + `CatalogViewModel`. One catalog for every customer; a
 * customer that negotiated a different price gets a contract price on their own page, which layers
 * over this. Two rules from the view model carry over:
 *
 *   * A BLANK SELL PRICE IS NOT ZERO. It means "no fixed price - bill at cost plus the customer's
 *     purchase markup". Reading "" as 0 would sell the item for nothing, so the form refuses a sell
 *     box that is filled in but not a number, and sends null for one that is empty.
 *   * REMOVE MAY RETIRE. An item that charges reference is kept, retired, so an invoice line can
 *     always say where its price came from - and the server's sentence saying so is shown, because
 *     the row does not leave an "include retired" list.
 *
 * Rows reflow rather than panning as the XAML's fixed-column sheet did.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import { CatalogItemKind, type CatalogItemDto } from '../contracts.gen.js';
import { join, money } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { moneyBox } from '../ui/MoneyBox.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkBox,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  hstack,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

const KINDS: readonly CatalogItemKind[] = [
  CatalogItemKind.Part,
  CatalogItemKind.Software,
  CatalogItemKind.Service,
  CatalogItemKind.Other
];
const KIND_NAMES = ['Part', 'Software', 'Service', 'Other'] as const;

function kindLabel(kind: CatalogItemKind): string {
  return KIND_NAMES[KINDS.indexOf(kind)] ?? 'Other';
}

export function catalogPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      /** 0 for a new item; the id being edited otherwise. */
      let editingId = 0;
      let busy = false;

      const status = new StatusLine();

      // ---- header ----
      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, h1('Catalog'));
      titleRow.controls.add(button('New item', () => beginNew(), true));
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        titleRow,
        muted('Parts, software and fixed-price services you sell. Used to price charges on a ticket.')
      );

      // ---- editor ----
      const editorTitle = h2('New catalog item');
      const skuBox = textBox();
      const nameBox = textBox();
      const kindPicker = comboBox<string>([...KIND_NAMES], { selectedIndex: 0 });
      const vendorBox = textBox();
      const cost = moneyBox();
      const sell = moneyBox();
      const activeCheck = checkBox('Active', true);
      const notesBox = textBox();
      const editorStatus = new StatusLine();

      const editor = card({ visible: false });
      editor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          editorTitle,
          fieldRow(field('Name', nameBox), field('SKU (optional)', skuBox)),
          fieldRow(field('Type', kindPicker), field('Vendor', vendorBox)),
          fieldRow(field('Cost', cost.row), field('Sell price', sell.row)),
          muted(
            "Leave Sell price blank to bill at cost plus the customer's purchase markup. A number fixes the price and the markup no longer applies."
          ),
          field('Notes', notesBox),
          activeCheck,
          stackOf(buttonRow(), button('Save', () => void save(), true), button('Cancel', () => closeEditor())),
          editorStatus.label
        )
      );

      // ---- filters ----
      const searchBox = textBox({ placeholder: 'Name, SKU or vendor' });
      searchBox.on('keydown', (e: { key: string }) => {
        if (e.key === 'Enter') void refresh();
      });
      const kindFilter = comboBox<string>(['Any type', ...KIND_NAMES], {
        selectedIndex: 0,
        onSelectionChanged: () => void refresh()
      });
      const retiredCheck = checkBox('Include retired', false);
      retiredCheck.on('change', () => void refresh());

      const filters = card();
      filters.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('Search', searchBox, { width: 240 }),
            field('Type', kindFilter, { width: 150 }),
            retiredCheck
          ),
          stackOf(buttonRow(), button('Apply', () => void refresh(), true), button('Reset', () => void reset()))
        )
      );

      // ---- list ----
      const list = new CardList<CatalogItemDto>({ key: i => i.id, build: i => buildItemCard(i) });
      const empty = new EmptyState('');

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(vstack({ spacing: GAP.lg }), header, status.label, editor, filters, empty.panel, list.panel)
      );
      host.controls.add(view);

      // ---------------------------------------------------------------

      /**
       * One column, top to bottom. A price and two buttons beside the name left the name a third of a
       * phone's width, and the SKU and type chip were cut off.
       */
      function buildItemCard(i: CatalogItemDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(i.name, { font: FONT.bold }));
        if (i.sku) heading.controls.add(value(i.sku, { font: FONT.small, role: 'muted' }));
        heading.controls.add(chip(kindLabel(i.kind), 'neutral'));
        if (!i.active) heading.controls.add(chip('retired', 'warn'));

        const body = stackOf(vstack({ spacing: GAP.xs }), heading);
        const detail = join([i.vendor, i.description]);
        if (detail) body.controls.add(muted(detail));
        // A blank sell price is the markup rule, said in words: "$0.00" would read as free.
        body.controls.add(
          text(
            join([
              i.listPrice === null ? "Sells at the customer's markup" : `Sells at ${money(i.listPrice)}`,
              `cost ${money(i.unitCost)}`
            ]),
            { font: FONT.small }
          )
        );
        body.controls.add(
          stackOf(buttonRow(), button('Edit', () => beginEdit(i)), button('Remove', () => void remove(i)))
        );

        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      // ---- editor ----

      function beginNew(): void {
        editingId = 0;
        editorTitle.text = 'New catalog item';
        skuBox.text = '';
        nameBox.text = '';
        kindPicker.selectedIndex = 0;
        vendorBox.text = '';
        cost.set(0);
        sell.set(null);
        activeCheck.checked = true;
        notesBox.text = '';
        openEditor();
      }

      function beginEdit(i: CatalogItemDto): void {
        editingId = i.id;
        editorTitle.text = `Edit ${i.name}`;
        // LiteDB reads a stored "" back as null, whatever the contract says.
        skuBox.text = i.sku ?? '';
        nameBox.text = i.name ?? '';
        kindPicker.selectedIndex = Math.max(0, KINDS.indexOf(i.kind));
        vendorBox.text = i.vendor ?? '';
        cost.set(i.unitCost);
        sell.set(i.listPrice);
        activeCheck.checked = i.active;
        notesBox.text = i.description ?? '';
        openEditor();
      }

      function openEditor(): void {
        editorStatus.clear();
        editor.visible = true;
        relayout(content);
        nameBox.focus();
      }

      function closeEditor(): void {
        editor.visible = false;
        relayout(content);
      }

      async function save(): Promise<void> {
        if (busy) return;
        const name = nameBox.text.trim();
        if (!name) {
          editorStatus.error('A catalog item needs a name.');
          return;
        }
        const unitCost = cost.value();
        if (unitCost === null || unitCost < 0) {
          editorStatus.error('Enter a unit cost of zero or more.');
          return;
        }
        let listPrice: number | null = null;
        if (sell.text().trim()) {
          listPrice = sell.value();
          if (listPrice === null || listPrice < 0) {
            editorStatus.error("Enter a sell price of zero or more, or leave it blank to use the customer's markup.");
            return;
          }
        }

        const body = {
          sku: skuBox.text.trim(),
          name,
          description: notesBox.text.trim(),
          kind: KINDS[kindPicker.selectedIndex] ?? CatalogItemKind.Part,
          unitCost,
          listPrice,
          vendor: vendorBox.text.trim(),
          active: activeCheck.checked
        };
        busy = true;
        editorStatus.busy('Saving…');
        try {
          if (editingId === 0) await crm.createCatalogItem(body);
          else await crm.updateCatalogItem(editingId, body);
          if (ctx.cancelled()) return;
          status.ok(editingId === 0 ? `Added ${name}.` : `Saved ${name}.`);
          closeEditor();
          await refresh();
        } catch (error) {
          // The form stays open with what was typed, so a duplicate SKU is fixed rather than retyped.
          if (!ctx.cancelled()) editorStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function remove(i: CatalogItemDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Remove item',
          message: `Remove "${i.name}" from the catalog? If charges already use it, it is retired instead, so those invoices keep their price.`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        status.busy(`Removing ${i.name}…`);
        try {
          const retired = await crm.deleteCatalogItem(i.id);
          if (ctx.cancelled()) return;
          status.ok(retired ?? `Removed ${i.name}.`);
          if (editingId === i.id) closeEditor();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      // ---- loading ----

      async function reset(): Promise<void> {
        searchBox.text = '';
        kindFilter.selectedIndex = 0;
        retiredCheck.checked = false;
        await refresh();
      }

      async function refresh(): Promise<void> {
        const kindIndex = kindFilter.selectedIndex;
        const filtered = searchBox.text.trim().length > 0 || kindIndex > 0;
        try {
          const items = await crm.searchCatalog(
            {
              search: searchBox.text,
              kind: kindIndex > 0 ? KINDS[kindIndex - 1] : undefined,
              includeInactive: retiredCheck.checked
            },
            ctx.dispose.signal()
          );
          if (ctx.cancelled()) return;
          list.set(items);
          empty.message = filtered
            ? 'Nothing matches that filter.'
            : 'No catalog items yet. Add the parts and licenses you sell, so charges can be priced from a list instead of typed each time.';
          empty.update(items.length);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the catalog: ' + describeError(error));
        }
      }

      void refresh();
    }
  };
}
